import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';
import { toast } from 'sonner';

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);

  const redirectPath = searchParams.get('redirect') || '/account';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const result = await login(formData.email, formData.password);
    
    if (result.success) {
      toast.success('Welcome Back!', {
        description: 'You have successfully logged in.'
      });
      navigate(redirectPath);
    } else {
      toast.error('Login Failed', {
        description: result.error
      });
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center py-16 px-4">
      <div className="max-w-md w-full">
        <div className="bg-[#141414] rounded-2xl shadow-xl shadow-[#D4AF37]/10 p-10 border border-[#D4AF37]/20">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-[#D4AF37]/30">
              <span className="text-[#0A0A0A] text-3xl font-bold font-heading">B</span>
            </div>
            <h1 className="text-4xl font-bold text-white mb-2 font-heading">Welcome Back</h1>
            <p className="text-lg text-[#888888] font-body">Sign in to your BOXVERRA account</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-base font-body text-white">Email Address</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#888888]" />
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="pl-12 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                  placeholder="you@example.com"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="password" className="text-base font-body text-white">Password</Label>
                <Link to="/forgot-password" className="text-sm text-[#D4AF37] hover:text-[#B8960C] font-body">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#888888]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="pl-12 pr-12 py-6 text-lg font-body border-2 border-[#D4AF37]/30 bg-[#0A0A0A] text-white placeholder-[#888888] focus:border-[#D4AF37]"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888] hover:text-white"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] py-6 text-xl font-bold font-body"
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>

          {/* Sign Up Link */}
          <p className="mt-8 text-center text-base text-[#888888] font-body">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#D4AF37] hover:text-[#B8960C] font-semibold">
              Create account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;